import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, patch, post, put } from '../../lib/api.ts';
import { FormSheet, FormSection, FormField } from '../../components/ui/FormTable.tsx';
import { ErrorText } from '../../components/AuthShell.tsx';
import type { StepDef } from '../../lib/approvals.ts';
import { LineEditor } from './LineEditor.tsx';
import { Button } from '../../components/ui/Button.tsx';

interface TypeRow { id: string; code: string; name: string; description: string; targetType: string | null; initiatorRoles: string[]; initiatorEditPolicy: 'fixed' | 'append_only' | 'free'; isActive: boolean; lines: Array<{ id: string; name: string; isDefault: boolean; isComplete: boolean; steps: Array<StepDef & { id: string }> }> }
const ROLES = ['tenant_admin', 'ciso', 'ciso_staff', 'dept_manager', 'auditor', 'viewer'];

/** CR-045: 결재 관련 테넌트 정책 — 현재는 위탁 업무 등록 결재 요구 하나 (M4 settings.outsourcing.registerApproval) */
function PolicyBox() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['vendors-settings'], queryFn: () => api<{ registerApproval: boolean }>('/vendors/settings') });
  const [v, setV] = useState<boolean | null>(null);
  const cur = v ?? q.data?.registerApproval ?? false;
  const save = useMutation({ mutationFn: () => put('/vendors/settings', { registerApproval: cur }), meta: { success: '결재 정책을 저장했습니다' }, onSuccess: () => { setV(null); qc.invalidateQueries({ queryKey: ['vendors-settings'] }); } });
  return (
    <FormSheet onSubmit={(e) => { e.preventDefault(); save.mutate(); }} actions={<Button type="submit" busy={save.isPending} disabled={v === null || v === q.data?.registerApproval}>저장</Button>}>
      <FormSection title="결재 정책" cols={1}>
        <FormField label="위탁 업무 등록" hint="켜면 위탁 업무 등록 시 「위탁 등록」 유형으로 기안되고 승인 후 활성됩니다. 끄면 즉시 활성"><label className="ft-check"><input type="checkbox" checked={cur} disabled={q.isLoading} onChange={(e) => setV(e.target.checked)} />신규 등록에 결재 요구</label></FormField>
      </FormSection>
      <ErrorText error={save.error ?? q.error} />
    </FormSheet>
  );
}

/** 결재 유형·결재선 설정 (tenant_admin). 단계마다 사람을 지정해야 결재선이 완성된다. */
export function ApprovalSettingsPage() {
  const qc = useQueryClient();
  const types = useQuery({ queryKey: ['approval-settings'], queryFn: () => api<TypeRow[]>('/approval-settings/types') });
  const [sel, setSel] = useState<string>('');
  const [steps, setSteps] = useState<StepDef[]>([]);
  const [msg, setMsg] = useState('');
  const type = types.data?.find((t) => t.id === sel);
  const line = type?.lines.find((l) => l.isDefault);
  useEffect(() => { if (!sel && types.data?.length) setSel(types.data[0].id); }, [types.data, sel]);
  useEffect(() => { if (line) setSteps(line.steps.map(({ id: _id, ...s }) => s)); }, [line?.id, line?.steps.length]); // eslint-disable-line
  const save = useMutation({ mutationFn: () => post<{ isComplete: boolean }>(`/approval-settings/lines/${line!.id}/steps`, { steps }), onSuccess: (r) => { setMsg(r.isComplete ? '저장했습니다. 결재선이 완성되었습니다.' : '저장했습니다. 아직 결재자가 지정되지 않은 단계가 있습니다.'); qc.invalidateQueries({ queryKey: ['approval-settings'] }); qc.invalidateQueries({ queryKey: ['approval-types'] }); } });
  const updType = useMutation({ mutationFn: (b: object) => patch(`/approval-settings/types/${sel}`, b), onSuccess: () => qc.invalidateQueries({ queryKey: ['approval-settings'] }) });

  return (
    <div className="">
      <PageHeader title="결재선 설정" description="결재자는 사람 단위로 지정합니다. 역할 힌트만 있고 사람이 비어 있는 단계가 있으면 그 유형으로 기안할 수 없습니다." />
      <div className="mb-3"><PolicyBox /></div>
      <div className="grid gap-4 md:grid-cols-[260px_1fr]">
        <ul className="border border-slate-200 divide-y divide-slate-200 text-sm">
          {types.data?.map((t) => { const d = t.lines.find((l) => l.isDefault); return (
            <li key={t.id}><button className={`w-full text-left px-3 py-2 ${sel === t.id ? 'bg-sky-50 font-semibold' : 'hover:bg-slate-50'}`} onClick={() => { setSel(t.id); setMsg(''); }}>
              <div>{t.name}{!t.isActive && <span className="ml-1 text-xs text-slate-400">(비활성)</span>}</div>
              <div className={`text-xs ${d?.isComplete ? 'text-green-700' : 'text-sky-700'}`}>{d?.isComplete ? '완성' : '미완성 — 결재자 지정 필요'}</div>
            </button></li>); })}
        </ul>
        {type && line && (
          <div>
            <div className="text-xs text-slate-500 mb-2"><code>{type.code}</code> · {type.description}{type.targetType && ` · 대상: ${type.targetType}`}</div>
            <div className="flex flex-wrap gap-3 items-center text-sm mb-3">
              <label>기안자 조정 <select className="border border-slate-300 px-2 py-1 text-xs ml-1" value={type.initiatorEditPolicy} onChange={(e) => updType.mutate({ initiatorEditPolicy: e.target.value })}><option value="fixed">고정</option><option value="append_only">단계 추가만</option><option value="free">자유</option></select></label>
              <label><input type="checkbox" checked={type.isActive} onChange={(e) => updType.mutate({ isActive: e.target.checked })} /> 사용</label>
              <span className="text-xs text-slate-500">기안 가능 역할:</span>
              {ROLES.map((r) => <label key={r} className="text-xs"><input type="checkbox" checked={type.initiatorRoles.includes(r)} onChange={(e) => updType.mutate({ initiatorRoles: e.target.checked ? [...type.initiatorRoles, r] : type.initiatorRoles.filter((x) => x !== r) })} /> {r}</label>)}
            </div>
            <div className="font-semibold text-sm mb-1">{line.name}</div>
            <LineEditor steps={steps} onChange={setSteps} policy="admin" />
            <div className="mt-3 flex items-center gap-3"><Button busy={save.isPending} onClick={() => save.mutate()}>저장</Button>{msg && <span className="text-sm text-green-700">{msg}</span>}</div>
            <ErrorText error={save.error ?? updType.error} />
          </div>
        )}
      </div>
    </div>
  );
}
